import {
  useOrderApprovalPoliciesMutation,
  workspaceApprovalPoliciesQuery,
} from "@common/api/approval-policies"
import type {
  ApprovalPolicyOrder,
  ApprovalPolicyPublic,
  ApprovalSubject,
} from "@common/client"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { Stamp } from "lucide-react"
import { z } from "zod"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListTabs } from "@/components/layout/list-tabs"
import { NameFilter } from "@/components/layout/name-filter"
import { PageContent, PageTitle } from "@/components/page/page-content"
import useCustomToast from "@/hooks/use-custom-toast"
import { MoveButtons } from "@/routes/_layout/settings/_panel/-components/editor-parts"
import {
  type Names,
  phraseText,
} from "@/routes/_layout/settings/_panel/-lib/flow"
import { PolicyCard } from "./-components/policy-card"
import {
  approverItem,
  byCheckOrder,
  policyPhrases,
  SUBJECT_LABELS,
  SUBJECTS,
} from "./-lib/approvals"
import {
  CREATE_FLOWS_PERMISSION,
  UPDATE_FLOWS_PERMISSION,
} from "./-lib/permission"

const searchSchema = z.object({
  search: z.string().optional().catch(""),
  subject: z.enum(SUBJECTS).optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/approval-flows/",
)({
  validateSearch: (search) => searchSchema.parse(search),
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(workspaceApprovalPoliciesQuery()),
  component: ApprovalFlowsPage,
})

function ApprovalFlowsPage(): React.ReactElement {
  const { search = "", subject } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data } = useSuspenseQuery(workspaceApprovalPoliciesQuery())
  const canCreate = useHasPermission(CREATE_FLOWS_PERMISSION)
  const canEdit = useHasPermission(UPDATE_FLOWS_PERMISSION)
  const order = useOrderApprovalPoliciesMutation()
  const showToast = useCustomToast()
  const ordered = withOrder(
    data.policies,
    order.isPending ? order.variables.approvalPolicyOrder : undefined,
  ).sort((first, second) => byCheckOrder(first.policy, second.policy))
  const needle = search.toLowerCase()
  const matching = ordered.filter((entry) =>
    searchText(entry, data.names).includes(needle),
  )
  const subjects = SUBJECTS.filter((candidate) =>
    matching.some((entry) => entry.policy.subject === candidate),
  )
  const selected =
    subjects.find((candidate) => candidate === subject) ?? subjects[0]
  const custom = customIds(ordered, selected)
  const reorderable = canEdit && !search && custom.length > 1
  const update = (next: { search?: string; subject?: ApprovalSubject }) =>
    navigate({
      search: (previous) => ({ ...previous, ...next }),
      replace: true,
    })
  const filter = (next: string) => update({ search: next || undefined })

  const move = (moving: ApprovalSubject, from: number, to: number) =>
    order.mutate(
      {
        approvalPolicyOrder: {
          subject: moving,
          policy_ids: swapped(custom, from, to),
        },
      },
      { onError: (error) => handleError(error, showToast) },
    )

  return (
    <PageContent width="full">
      <PageTitle
        title="Approval flows"
        description="Each flow says who must approve something, and in which order. When something needs approval, Fleetcraft uses the first flow from the top whose conditions all hold, and the default flow when none do."
        action={
          <div className="flex shrink-0 items-center gap-2">
            <NameFilter
              search={search}
              onSearch={filter}
              placeholder="Filter flows…"
            />
            {canCreate && (
              <Button asChild size="sm">
                <Link
                  to="/settings/workspace/approval-flows/create"
                  search={{ subject: selected }}
                >
                  New flow
                </Link>
              </Button>
            )}
          </div>
        }
      />
      {selected && (
        <div className="overflow-x-auto">
          <ListTabs
            tabs={subjects}
            tab={selected}
            label={(tab) => SUBJECT_LABELS[tab].label}
            name="Approval"
            onSelect={(next) => update({ subject: next })}
          />
        </div>
      )}
      {selected ? (
        <div className="space-y-3">
          {matching
            .filter((entry) => entry.policy.subject === selected)
            .map((entry) => (
              <PolicyCard
                key={entry.policy.id ?? entry.policy.name}
                entry={entry}
                names={data.names}
              >
                {reorderable && !entry.policy.is_default && entry.policy.id && (
                  <PolicyMove
                    position={custom.indexOf(entry.policy.id)}
                    count={custom.length}
                    name={entry.policy.name}
                    disabled={order.isPending}
                    onMove={(from, to) => move(selected, from, to)}
                  />
                )}
              </PolicyCard>
            ))}
        </div>
      ) : (
        <ListEmpty
          icon={Stamp}
          title="No matching flows"
          body="Try another flow, step or person."
          onClear={() => filter("")}
        />
      )}
    </PageContent>
  )
}

function PolicyMove({
  position,
  count,
  name,
  disabled,
  onMove,
}: {
  position: number
  count: number
  name: string
  disabled: boolean
  onMove: (from: number, to: number) => void
}): React.ReactElement {
  return (
    <MoveButtons
      label={name}
      canMoveUp={!disabled && position > 0}
      canMoveDown={!disabled && position < count - 1}
      onMove={(offset) => onMove(position, position + offset)}
    />
  )
}

function swapped(ids: string[], from: number, to: number): string[] {
  return ids.map(
    (id, index) =>
      (index === from ? ids[to] : index === to ? ids[from] : id) ?? id,
  )
}

function customIds(
  ordered: ApprovalPolicyPublic[],
  subject: ApprovalSubject | undefined,
): string[] {
  return ordered.flatMap(({ policy }) =>
    policy.subject === subject && !policy.is_default && policy.id
      ? [policy.id]
      : [],
  )
}

function withOrder(
  entries: ApprovalPolicyPublic[],
  pending: ApprovalPolicyOrder | undefined,
): ApprovalPolicyPublic[] {
  return entries.map((entry) => {
    const rank =
      pending?.subject === entry.policy.subject && entry.policy.id
        ? pending.policy_ids.indexOf(entry.policy.id)
        : -1
    return rank < 0
      ? entry
      : { ...entry, policy: { ...entry.policy, priority: rank } }
  })
}

function searchText({ policy }: ApprovalPolicyPublic, names: Names): string {
  return [
    policy.name,
    SUBJECT_LABELS[policy.subject].label,
    ...policy.steps.flatMap((step) => [
      step.name,
      ...(step.audiences ?? []).map(
        (audience) => approverItem(audience, names).label,
      ),
    ]),
    ...policyPhrases(policy, names).map(phraseText),
  ]
    .join(" ")
    .toLowerCase()
}
